import React, { useState } from 'react';
import {
  List,
  ListItemButton,
  ListItemText,
  Collapse,
  Typography,
  Box,
  Skeleton,
  useTheme,
} from '@mui/material';
import {
  ExpandLess,
  ExpandMore,
  ChevronRight,
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';

const NavigationList = ({ navigation, navigationLoading, onNavigate }) => {
  const theme = useTheme();
  const { t, i18n } = useTranslation();

  // State for managing expanded sections
  const [expandedDepartments, setExpandedDepartments] = useState({});
  const [expandedCategories, setExpandedCategories] = useState({});

  const toggleDepartment = (deptId) => {
    setExpandedDepartments(prev => ({
      ...prev,
      [deptId]: !prev[deptId]
    }));
  };

  const toggleCategory = (categoryId) => {
    setExpandedCategories(prev => ({
      ...prev,
      [categoryId]: !prev[categoryId]
    }));
  };

  const handleDepartmentClick = (dept) => {
    onNavigate(`/search?department_slugs=${dept.slug}`);
  };

  const handleCategoryClick = (category, deptSlug) => {
    onNavigate(`/search?category_slugs=${category.slug}&department_slugs=${deptSlug}`);
  };

  const handleSubCategoryClick = (subCategory, deptSlug) => {
    onNavigate(`/search?category_slugs=${subCategory.slug}&department_slugs=${deptSlug}`);
  };

  if (navigationLoading) {
    return (
      <List>
        {[1, 2, 3, 4, 5].map((item) => (
          <ListItemButton key={item} sx={{ py: 1.5 }}>
            <Skeleton variant="text" width="60%" height={24} />
          </ListItemButton>
        ))}
      </List>
    );
  }

  if (!navigation?.departments && !navigation?.uncategorized) {
    return null;
  }

  return (
    <List>
      {/* Departments */}
      {navigation?.departments?.map((dept) => {
        const hasCategories = dept.categories && dept.categories.length > 0;
        const isExpanded = expandedDepartments[dept.id];

        return (
          <Box key={dept.id}>
            <ListItemButton
              onClick={() => {
                if (hasCategories) {
                  toggleDepartment(dept.id);
                } else {
                  handleDepartmentClick(dept);
                }
              }}
              sx={{
                py: 1.5,
                '&:hover': {
                  backgroundColor: theme.palette.action.hover,
                },
              }}
            >
              <ListItemText
                primary={
                  <Typography
                    variant="body1"
                    sx={{
                      fontWeight: hasCategories ? 600 : 500,
                      color: theme.palette.text.primary,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                    }}
                  >
                    {dept.name[i18n.language] || dept.name.en}
                    {hasCategories && (
                      isExpanded ? <ExpandLess /> : <ExpandMore />
                    )}
                  </Typography>
                }
              />
            </ListItemButton>

            {/* Categories Collapse */}
            {hasCategories && (
              <Collapse in={isExpanded} timeout="auto" unmountOnExit>
                <List component="div" disablePadding>
                  {dept.categories.map((category) => {
                    const hasChildren = category.children && category.children.length > 0;
                    const isCatExpanded = expandedCategories[`dept-${dept.id}-cat-${category.id}`];

                    return (
                      <Box key={category.id}>
                        <ListItemButton
                          sx={{ pl: 4, py: 1.25 }}
                          onClick={() => {
                            if (hasChildren) {
                              toggleCategory(`dept-${dept.id}-cat-${category.id}`);
                            } else {
                              handleCategoryClick(category, dept.slug);
                            }
                          }}
                        >
                          <ListItemText
                            primary={
                              <Typography
                                variant="body2"
                                sx={{
                                  fontWeight: hasChildren ? 500 : 400,
                                  color: theme.palette.text.secondary,
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'space-between',
                                }}
                              >
                                {category.name[i18n.language] || category.name.en}
                                {hasChildren && (
                                  isCatExpanded ? <ExpandLess /> : <ChevronRight />
                                )}
                              </Typography>
                            }
                          />
                        </ListItemButton>

                        {/* Sub Categories Collapse */}
                        {hasChildren && (
                          <Collapse in={isCatExpanded} timeout="auto" unmountOnExit>
                            <List component="div" disablePadding>
                              {category.children.map((child) => (
                                <ListItemButton
                                  key={child.id}
                                  sx={{ pl: 6, py: 1 }}
                                  onClick={() => handleSubCategoryClick(child, dept.slug)}
                                >
                                  <ListItemText
                                    primary={
                                      <Typography
                                        variant="body2"
                                        sx={{
                                          fontWeight: 400,
                                          color: theme.palette.text.secondary,
                                        }}
                                      >
                                        {child.name[i18n.language] || child.name.en}
                                      </Typography>
                                    }
                                  />
                                </ListItemButton>
                              ))}
                            </List>
                          </Collapse>
                        )}
                      </Box>
                    );
                  })}
                </List>
              </Collapse>
            )}
          </Box>
        );
      })}

      {/* Uncategorized Categories */}
      {navigation?.uncategorized?.length > 0 && (
        <Box>
          <ListItemButton
            onClick={() => toggleDepartment('uncategorized')}
            sx={{
              py: 1.5,
              '&:hover': {
                backgroundColor: theme.palette.action.hover,
              },
            }}
          >
            <ListItemText
              primary={
                <Typography
                  variant="body1"
                  sx={{
                    fontWeight: 600,
                    color: theme.palette.text.primary,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                  }}
                >
                  {t('navigation.moreCategories', 'More Categories')}
                  {expandedDepartments['uncategorized'] ? <ExpandLess /> : <ExpandMore />}
                </Typography>
              }
            />
          </ListItemButton>

          <Collapse in={expandedDepartments['uncategorized']} timeout="auto" unmountOnExit>
            <List component="div" disablePadding>
              {navigation.uncategorized.map((category) => {
                const hasChildren = category.children && category.children.length > 0;
                const isCatExpanded = expandedCategories[`uncat-${category.id}`];

                return (
                  <Box key={category.id}>
                    <ListItemButton
                      sx={{ pl: 4, py: 1.25 }}
                      onClick={() => {
                        if (hasChildren) {
                          toggleCategory(`uncat-${category.id}`);
                        } else {
                          onNavigate(`/search?category_slugs=${category.slug}`);
                        }
                      }}
                    >
                      <ListItemText
                        primary={
                          <Typography
                            variant="body2"
                            sx={{
                              fontWeight: hasChildren ? 500 : 400,
                              color: theme.palette.text.secondary,
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'space-between',
                            }}
                          >
                            {category.name[i18n.language] || category.name.en}
                            {hasChildren && (
                              isCatExpanded ? <ExpandLess /> : <ChevronRight />
                            )}
                          </Typography>
                        }
                      />
                    </ListItemButton>

                    {/* Sub Categories for Uncategorized */}
                    {hasChildren && (
                      <Collapse in={isCatExpanded} timeout="auto" unmountOnExit>
                        <List component="div" disablePadding>
                          {category.children.map((child) => (
                            <ListItemButton
                              key={child.id}
                              sx={{ pl: 6, py: 1 }}
                              onClick={() => onNavigate(`/search?category_slugs=${child.slug}`)}
                            >
                              <ListItemText
                                primary={
                                  <Typography
                                    variant="body2"
                                    sx={{
                                      fontWeight: 400,
                                      color: theme.palette.text.secondary,
                                    }}
                                  >
                                    {child.name[i18n.language] || child.name.en}
                                  </Typography>
                                }
                              />
                            </ListItemButton>
                          ))}
                        </List>
                      </Collapse>
                    )}
                  </Box>
                );
              })}
            </List>
          </Collapse>
        </Box>
      )}
    </List>
  );
};

export default NavigationList;
